import { z } from 'zod';

import { createOffsetPaginationRequestQuerySchema } from './pagination';
import { PrimaryKeySchema, commaDelimitedList } from './utils';

const TeamNameSchema = z.string().min(1).max(255);

export const TeamSchema = z.object({
  teamId: PrimaryKeySchema,
  name: TeamNameSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type Team = z.infer<typeof TeamSchema>;

export const TeamsSchema = z.array(TeamSchema);

export type Teams = z.infer<typeof TeamsSchema>;

export const TeamsQuerySchema = z
  .object({
    teamIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
    name: z.string().optional(),
  })
  .merge(
    createOffsetPaginationRequestQuerySchema(
      z.enum(['createdAt', 'updatedAt', 'name']),
    ),
  );

export type TeamsQuery = z.infer<typeof TeamsQuerySchema>;

export const CreateTeamRequestSchema = z.object({
  name: TeamNameSchema,
});

export type CreateTeamRequest = z.infer<typeof CreateTeamRequestSchema>;

export const UpdateTeamRequestSchema = z.object({
  name: TeamNameSchema,
});

export type UpdateTeamRequest = z.infer<typeof UpdateTeamRequestSchema>;

export const TeamBillingSchema = z.object({
  teamId: TeamSchema.shape.teamId,
  billingPageUrl: z.string(),
  subscriptions: z.array(
    z.object({
      status: z.enum([
        'active',
        'canceled',
        'incomplete',
        'incomplete_expired',
        'past_due',
        'trialing',
        'unpaid',
        'paused',
      ]),
      current_period_end: z.coerce.date(),
      current_period_start: z.coerce.date(),
      cancel_at_period_end: z.boolean().optional(),
      canceled_at: z.coerce.date().nullable().optional(),
      trial_start: z.coerce.date().nullable().optional(),
      trial_end: z.coerce.date().nullable().optional(),
      items: z.array(
        z.object({
          quantity: z.number().optional(),
          price: z.object({
            nickname: z.string().optional(),
            product_name: z.string().optional(),
            unit_amount: z.number().optional(),
            currency: z.string().optional(),
            recurring: z
              .object({
                interval: z.enum(['day', 'week', 'month', 'year']).optional(),
                interval_count: z.number().optional(),
              })
              .optional(),
          }),
        }),
      ),
    }),
  ),
});

export type TeamBilling = z.infer<typeof TeamBillingSchema>;
